import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { useFormUser, useOnBehalfSubmitter } from '../../../container/onBehalfContext';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../constants';
import {
  ATTACH_ON_INITIATION_KEY,
  AUTO_ADD_INFO_FOR,
  SUBMITTER_2_STRICT_ORDER_STEP,
} from '../../from-builder/elements-settings/submitters-field-element-settings';
import { getTokenFromSearch } from '../../../utility/notificationLink';

export const SubmittersFieldElement = function FromElement({
  element,
  isSubmitter,
  handlerChange,
  submitterAddInfoElementID,
  strictOrderStep,
  submitter2Info,
}) {
  const { options } = element ?? {};
  // The on-behalf submitter when the form is filled in from a "Sent" row of the
  // Submissions folder, the logged-in user otherwise.
  const user = useFormUser();
  const onBehalfOf = useOnBehalfSubmitter();
  const location = useLocation();
  const { pathname, search } = location;
  const autoAddInfoFor = options?.autoAddInfoFor?.value;
  const valueInput = element?.options?.valueInput;
  const isInitForm = pathname.toLowerCase().includes('initialization-form');
  // Submitting on someone's behalf happens in a modal on the Submissions folder,
  // where the route says nothing; the on-behalf submitter does.
  const isSendForm = pathname.toLowerCase().includes('form-view') || !!onBehalfOf;

  const needShowInitiatorInfo = (isSubmitter && submitterAddInfoElementID == element.id) || (isInitForm && autoAddInfoFor === 'initiator');
  const needShowSubmitterInfo = isSendForm && autoAddInfoFor === AUTO_ADD_INFO_FOR.SUBMITTER && submitterAddInfoElementID == element.id;
  // Step-bound, so unlike "Submitter" it needs no first-empty-field bookkeeping:
  // it fills for whoever submits at step 2 and stays empty on every other step.
  // `strictOrderStep` is null on a form without a strict order, which is how the
  // setting ends up ignored there.
  const needShowSubmitter2Info =
    isSendForm && autoAddInfoFor === AUTO_ADD_INFO_FOR.SUBMITTER_2 && strictOrderStep === SUBMITTER_2_STRICT_ORDER_STEP;
  const needShowUserInfo = needShowInitiatorInfo || needShowSubmitterInfo || needShowSubmitter2Info;
  const attachesOnInitiation = Boolean(options?.isFormName && options?.[ATTACH_ON_INITIATION_KEY]);
  // A "Submitter 2" field set to "Attach as soon as the form is initiated" already
  // shows, before step 2, the one person step 2 names -- the get-form's
  // `submitter_2_info`, absent when step 2 is a group. Step 2's own fill replaces it.
  const needShowNamedSubmitter2Info =
    isSendForm &&
    autoAddInfoFor === AUTO_ADD_INFO_FOR.SUBMITTER_2 &&
    attachesOnInitiation &&
    strictOrderStep != null &&
    strictOrderStep < SUBMITTER_2_STRICT_ORDER_STEP &&
    Boolean(submitter2Info);

  const getToken = () => getTokenFromSearch(search);

  function parseJwt(token) {
    if (token) {
      let base64Url = token.split('.')[1];
      let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      let jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(''),
      );

      return JSON.parse(jsonPayload);
    }
    return null;
  }

  const getSubmitterText = useCallback(
    (person, date) => {
      let submitter = '';

      element.options.labels.forEach((item) => {
        switch (item.value) {
          case 'name': {
            submitter = submitter + `${person?.first_name}`;
            break;
          }
          case 'surname': {
            submitter = submitter + ` ${person?.last_name}`;
            break;
          }
          case 'position': {
            if (person?.position?.name) {
              submitter = submitter + ` ${person?.position?.name}`;
            }
            break;
          }
          case 'date': {
            submitter = submitter + ` ${date}`;
            break;
          }
        }
      });

      return submitter;
    },
    [element],
  );

  const getCurrentUserText = useCallback(() => {
    if (parseJwt(getToken())?.sub_ins === 'UnregisteredSubmitter' || onBehalfOf?.submitter_type === 'UNREGISTERED') {
      return 'Unregistered Submitter';
    }

    return getSubmitterText(user, dayjs().format(DATE_FORMAT));
  }, [getSubmitterText, getToken, onBehalfOf?.submitter_type, user]);

  useLayoutEffect(() => {
    if (needShowNamedSubmitter2Info) {
      if (!options.valueInput) {
        const { position, date, ...names } = submitter2Info;
        handlerChange(element, getSubmitterText({ ...names, position: position ? { name: position } : null }, date));
      }
      return;
    }

    if (!needShowUserInfo) {
      return;
    }

    if (!options.valueInput) {
      handlerChange(element, getCurrentUserText());
      return;
    }

    // What step 1 carried in for the person step 2 names was only provisional,
    // dated the day of the initiation: whoever actually fills step 2 replaces it.
    if (needShowSubmitter2Info && attachesOnInitiation) {
      const text = getCurrentUserText();

      if (text !== options.valueInput) {
        handlerChange(element, text);
      }
    }
  }, [
    attachesOnInitiation,
    element,
    getCurrentUserText,
    getSubmitterText,
    handlerChange,
    needShowInitiatorInfo,
    needShowNamedSubmitter2Info,
    needShowSubmitterInfo,
    needShowSubmitter2Info,
    needShowUserInfo,
    options.valueInput,
    submitter2Info,
    submitterAddInfoElementID,
  ]);

  return (
    <>
      <label className="form-label" style={{ zIndex: 5000 + (element.options.zIndex || 10), width: 'fit-content', position: 'relative' }}>
        {element.options.name ? element.options.name : `Select${element.options.labels.length > 0 && 'ed'} Submitters Field`}
      </label>
      <p className={'form-submitters'}>
        {valueInput?.length ? element.options.valueInput : element.options.labels.map((item) => item.label).join(' ')}
      </p>
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}
    </>
  );
};
